/**
 * @file DownloadButton.tsx
 * @description Reusable button component that provides download options for content.
 * Supports multiple download formats and different style variants.
 */

import React, { useState } from "react";

type ButtonVariant = "primary" | "success" | "outline-white";

interface DownloadButtonProps {
  onDownload: (format: "docx" | "wxr") => Promise<void>;
  isLoading?: boolean;
  disabled?: boolean;
  variant?: ButtonVariant;
  className?: string;
  /**
   * Optional callback invoked when the dropdown open state changes.
   * Useful for parent components that need to adjust layout when the dropdown is open.
   */
  onOpenChange?: (isOpen: boolean) => void;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: "px-6 text-base rounded-full btn btn-primary",
  success:
    "px-6 text-base border-none rounded-full text-primary-content btn btn-success",
  "outline-white":
    "text-lg text-white border-white rounded-full btn btn-outline hover:text-primary hover:bg-white hover:border-white",
};

const DownloadButton: React.FC<DownloadButtonProps> = ({
  onDownload,
  isLoading = false,
  disabled = false,
  variant = "primary",
  className = "",
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeFormat, setActiveFormat] = useState<"docx" | "wxr" | null>(null);

  const handleToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    onOpenChange?.(newIsOpen);
  };

  const handleDownload = async (format: "docx" | "wxr") => {
    try {
      setActiveFormat(format);
      await onDownload(format);
    } finally {
      setActiveFormat(null);
      setIsOpen(false);
      onOpenChange?.(false);
    }
  };

  return (
    <div className={`dropdown dropdown-end ${className}`}>
      <button
        tabIndex={0}
        className={variantStyles[variant]}
        disabled={disabled || isLoading}
        onClick={handleToggle}
      >
        {isLoading ? (
          <span className="loading loading-spinner"></span>
        ) : (
          <>
            Scarica
            <svg
              className="w-4 h-4 ml-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </>
        )}
      </button>
      {isOpen && (
        <ul className="p-2 shadow dropdown-content menu bg-base-100 rounded-box">
          <li>
            <button
              onClick={() => handleDownload("docx")}
              disabled={isLoading}
              className="flex items-center gap-2 text-base-content"
            >
              {activeFormat === "docx" && (
                <span className="loading loading-spinner loading-xs"></span>
              )}
              Documento Word
            </button>
          </li>
          <li>
            <button
              onClick={() => handleDownload("wxr")}
              disabled={isLoading}
              className="flex items-center gap-2 text-base-content"
            >
              {activeFormat === "wxr" && (
                <span className="loading loading-spinner loading-xs"></span>
              )}
              WordPress XML
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

export default DownloadButton;
